import { Spinner, Modal } from '@fluentui/react';
import {
  OpenProjectModalContent,
  OpenProjectModalHeader,
  ProjectItem,
  ProjectList,
  modalStyles,
} from './styles';
import { useCallback, useContext, useEffect, useState } from 'react';
import { callFunction } from '../../../../lib/Helpers';
import { Trash, X } from '@phosphor-icons/react';
import {
  Block,
  ProjectInfo,
  TtsContext,
} from '../../../../contexts/TtsContext';
import { useAppInsightsContext } from '@microsoft/applicationinsights-react-js';
import {
  Button,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  DialogTrigger,
} from '@fluentui/react-components';
import { TeamsFxContext } from '../../../../Context';
import { useStore } from '../../../../store';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';

interface OpenProjectModalProps {
  isOpen: boolean;
  closeModal: () => void;
}

export const OpenProjectModal = ({
  isOpen,
  closeModal,
}: OpenProjectModalProps) => {
  const appInsights = useAppInsightsContext();
  const teamsUserCredential = useContext(TeamsFxContext).teamsUserCredential;

  const { loadProject, setProjectInfo, projectInfo } = useStore();

  const { t } = useTranslation('myapps');

  const [projectOptions, setProjectOptions] = useState<ProjectInfo[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [toDeleteProjectId, setToDeleteProjectId] = useState('');

  const loadProjects = useCallback(() => {
    setIsLoading(true);

    callFunction(
      true,
      'getUserProjects?{0}&appId=798cf522-7099-4391-b739-60e013002413',
      undefined,
      false,
      teamsUserCredential
    )
      .then((data) => {
        setProjectOptions(data);
      })
      .catch((error) => {
        if (process.env.REACT_APP_APP_INSIGHTS !== '')
          appInsights.trackException({
            exception: error,
            properties: { component: 'TtsApp', location: 'onLoadProjects' },
          });
      })
      .finally(() => setIsLoading(false));
  }, []);

  useEffect(() => {
    loadProjects();
  }, [loadProjects, projectInfo]);

  function deleteProject(id: string) {
    callFunction(
      true,
      'deleteProject?{0}&id=' + id,
      undefined,
      false,
      teamsUserCredential
    )
      .then(() => {
        setProjectInfo(undefined);
        loadProjects();
        toast.done(t('TTS_DELETE_PROJECT_SUCCESS'));
      })
      .catch((error) => {
        if (process.env.REACT_APP_APP_INSIGHTS !== '')
          appInsights.trackException({
            exception: error,
            properties: {
              component: 'TtsApp',
              location: 'onDeleteProject',
            },
          });
      });
  }

  function openProject(project: ProjectInfo) {
    loadProject(project.id, teamsUserCredential);
    closeModal();
  }

  return (
    <Modal
      isOpen={isOpen}
      onDismiss={closeModal}
      isBlocking={false}
      containerClassName={modalStyles.container}
    >
      <OpenProjectModalContent>
        <OpenProjectModalHeader>
          <h1>{t('TTS_OPEN_PROJECT_MODAL_TITLE')}</h1>
          <h2>{t('TTS_OPEN_PROJECT_MODAL_SUBTITLE')}</h2>
          <X className="closeModal" size={24} onClick={closeModal} />
        </OpenProjectModalHeader>

        <ProjectList>
          {isLoading ? (
            <Spinner size={3} label={t('TTS_OPEN_PROJECT_MODAL_SPINNER')} />
          ) : projectOptions.length <= 0 ? (
            <p>{t('TTS_OPEN_PROJECT_MODAL_NO_PROJECTS_MESSAGE')}</p>
          ) : (
            projectOptions.map((project) => (
              <ProjectItem
                key={'project-' + project.id}
                onClick={(e) => {
                  e.stopPropagation();
                  openProject(project);
                }}
              >
                <div className="content">
                  <h2>{project.name}</h2>
                  <p>{project.description}</p>
                </div>
                <Dialog modalType="alert">
                  <DialogTrigger disableButtonEnhancement>
                    <button
                      className="delete"
                      onClick={(e) => {
                        e.stopPropagation();
                        setToDeleteProjectId(project.id);
                      }}
                    >
                      <Trash size={24} weight="duotone" />
                    </button>
                  </DialogTrigger>
                  <DialogSurface>
                    <DialogBody>
                      <DialogTitle>
                        {t('TTS_DELETE_PROJECT_DIALOG_TITLE')}
                      </DialogTitle>
                      <DialogContent>
                        {t('TTS_DELETE_PROJECT_DIALOG_MESSAGE')}
                      </DialogContent>
                      <DialogActions>
                        <DialogTrigger disableButtonEnhancement>
                          <Button appearance="secondary">
                            {t('TTS_DELETE_PROJECT_CANCEL_BUTTON')}
                          </Button>
                        </DialogTrigger>
                        <Button
                          appearance="primary"
                          onClick={() => deleteProject(toDeleteProjectId)}
                        >
                          {t('TTS_DELETE_PROJECT_DELETE_BUTTON')}
                        </Button>
                      </DialogActions>
                    </DialogBody>
                  </DialogSurface>
                </Dialog>
              </ProjectItem>
            ))
          )}
        </ProjectList>
      </OpenProjectModalContent>
    </Modal>
  );
};
